import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key, Copy, Eye, EyeOff, RefreshCw, CheckCircle,
  Code, Globe, Zap, Shield, AlertTriangle, X,
  Terminal, BookOpen, Lock, Bell, Activity, List
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};
const INNER = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 10,
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: '#0D0D12',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, padding: '10px 14px',
  color: '#fff', fontSize: 13, outline: 'none',
  fontFamily: 'Inter, sans-serif',
};

const ENDPOINTS = [
  { method:'GET',  path:'/api/v1/info',                        desc:'API info & merchant details'            },
  { method:'GET',  path:'/api/v1/rates',                       desc:'Get live USDT exchange rates'           },
  { method:'GET',  path:'/api/v1/rates?currency=NGN',          desc:'Get rate for specific currency'         },
  { method:'GET',  path:'/api/v1/wallet',                      desc:'Get wallet balance'                     },
  { method:'GET',  path:'/api/v1/banks?currency=NGN',          desc:'Get supported banks for currency'       },
  { method:'GET',  path:'/api/v1/banks?currency=GHS&type=momo',desc:'Get mobile money operators'             },
  { method:'POST', path:'/api/v1/withdraw',                    desc:'Send payout to bank or mobile money'   },
  { method:'GET',  path:'/api/v1/withdraw/{reference}',        desc:'Check withdrawal status'               },
  { method:'POST', path:'/api/v1/collect',                     desc:'Collect GHS mobile money from customer' },
  { method:'GET',  path:'/api/v1/transactions',                desc:'Get transaction history'               },
  { method:'POST', path:'/api/v1/usdt/address',                desc:'Generate USDT sub-address for deposit' },
  { method:'GET',  path:'/api/v1/usdt/address/{reference}',    desc:'Get sub-address details & balance'    },
  { method:'GET',  path:'/api/v1/usdt/addresses',              desc:'List all USDT sub-addresses'          },
  { method:'POST', path:'/api/v1/webhooks/configure',          desc:'Configure webhook URL'                 },
  { method:'GET',  path:'/api/v1/webhooks/config',             desc:'Get webhook configuration'             },
  { method:'POST', path:'/api/v1/webhooks/test',               desc:'Send test webhook event'               },
  { method:'GET',  path:'/api/v1/webhooks/logs',               desc:'Get webhook delivery logs'             },
  { method:'POST', path:'/api/v1/webhooks/disable',            desc:'Disable webhook notifications'         },
];

const CODE_EXAMPLES = {
  rates: `curl -X GET https://kudibot.io/api/v1/rates \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"`,

  wallet: `curl -X GET https://kudibot.io/api/v1/wallet \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"`,

  banks: `curl -X GET "https://kudibot.io/api/v1/banks?currency=NGN&type=bank" \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"`,

  withdraw: `curl -X POST https://kudibot.io/api/v1/withdraw \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount_fiat"    : 5000,
    "currency"       : "NGN",
    "method"         : "bank",
    "account_number" : "0123456789",
    "account_name"   : "John Doe",
    "bank_code"      : "000014",
    "bank_name"      : "Access Bank",
    "reference"      : "MY-REF-001",
    "narration"      : "Payment for services"
  }'`,

  momo: `curl -X POST https://kudibot.io/api/v1/withdraw \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount_fiat"    : 20,
    "currency"       : "GHS",
    "method"         : "momo",
    "account_number" : "0551234567",
    "account_name"   : "Jane Doe",
    "bank_code"      : "MTN",
    "phone_number"   : "0551234567",
    "network"        : "MTN Mobile Money",
    "reference"      : "MY-REF-002"
  }'`,

  webhook: `curl -X POST https://kudibot.io/api/v1/webhooks/configure \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url"    : "https://yourapp.com/webhooks/kudibot",
    "secret" : "your_webhook_secret",
    "events" : "payout.success,payout.failed,payout.pending"
  }'`,

  verify: `# Python - Verify webhook signature
import hmac, hashlib
from flask import request

def verify_webhook(payload: str, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode(),
        payload.encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)

@app.route("/webhooks/kudibot", methods=["POST"])
def handle_webhook():
    payload   = request.get_data(as_text=True)
    signature = request.headers.get("X-Kudibot-Signature", "")
    secret    = "YOUR_WEBHOOK_SECRET"

    if not verify_webhook(payload, signature, secret):
        return {"error": "Invalid signature"}, 401

    data  = request.json
    event = data["event"]

    if event == "payout.success":
        reference = data["data"]["reference"]
        amount    = data["data"]["amount_fiat"]
        currency  = data["data"]["currency"]
        print(f"Payout {reference} of {currency} {amount} succeeded!")

    elif event == "payout.failed":
        print(f"Payout failed: {data['data']['reference']}")

    return {"received": True}, 200`,

  collect: `curl -X POST https://kudibot.io/api/v1/collect \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount_ghs"    : 100,
    "phone"         : "0241234567",
    "subscriber_no" : "0241234567",
    "network"       : "MTN",
    "customer_name" : "John Doe",
    "email"         : "john@example.com",
    "reference"     : "MY-COL-001",
    "description"   : "Payment for order #123"
  }'`,

  transactions: `curl -X GET "https://kudibot.io/api/v1/transactions?page=1&limit=20" \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"`,

  usdt_address: `# 1. Generate a unique USDT deposit address
curl -X POST https://kudibot.io/api/v1/usdt/address \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "reference" : "invoice-001",
    "label"     : "Order #1234 — John Doe"
  }'

# 2. Check if payment has arrived
curl -X GET https://kudibot.io/api/v1/usdt/address/invoice-001 \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"

# 3. List all generated addresses
curl -X GET https://kudibot.io/api/v1/usdt/addresses \\
  -H "X-Api-Key: YOUR_PUBLIC_KEY" \\
  -H "X-Secret-Key: YOUR_SECRET_KEY"`,
};

const WEBHOOK_PAYLOAD_PAYOUT = `{
  "event"     : "payout.success",
  "timestamp" : "2026-05-02T18:42:40.441095",
  "reference" : "KB-A1B2C3D4E5F6",
  "data"      : {
    "reference"      : "KB-A1B2C3D4E5F6",
    "status"         : "completed",
    "amount_fiat"    : 5000.00,
    "currency"       : "NGN",
    "amount_usdt"    : 3.69,
    "rate"           : 1353.19,
    "fees"           : 50,
    "method"         : "bank",
    "account_number" : "0123456789",
    "account_name"   : "John Doe",
    "bank_code"      : "000014",
    "bank_name"      : "Access Bank",
    "message"        : "Transfer successful"
  }
}`;

const WEBHOOK_PAYLOAD_COLLECTION = `{
  "event"     : "collection.success",
  "timestamp" : "2026-05-02T19:10:15.123456",
  "reference" : "MY-COL-001",
  "data"      : {
    "reference"     : "MY-COL-001",
    "status"        : "completed",
    "amount_ghs"    : 100.00,
    "amount_usdt"   : 7.36,
    "currency"      : "GHS",
    "network"       : "MTN",
    "phone"         : "0241234567",
    "customer_name" : "John Doe"
  }
}`;

const WEBHOOK_PAYLOAD_USDT = `{
  "event"     : "usdt.deposit",
  "timestamp" : "2026-06-06T14:22:10.000000",
  "reference" : "invoice-001",
  "data"      : {
    "reference"   : "invoice-001",
    "label"       : "Order #1234 — John Doe",
    "address"     : "TXxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    "amount_usdt" : 50.00,
    "tx_id"       : "b1db59686aae15441bfa36bc...",
    "status"      : "completed"
  }
}`;

export default function Developer() {
  const [apiKey,          setApiKey]          = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [accessDenied,    setAccessDenied]    = useState(false);
  const [generating,      setGenerating]      = useState(false);
  const [showSecret,      setShowSecret]      = useState(false);
  const [newKeys,         setNewKeys]         = useState(null);
  const [confirmModal,    setConfirmModal]    = useState(false);
  const [activeTab,       setActiveTab]       = useState('overview');
  const [activeCode,      setActiveCode]      = useState('rates');
  const [copied,          setCopied]          = useState('');

  const [webhookConfig,      setWebhookConfig]      = useState(null);
  const [webhookUrl,         setWebhookUrl]         = useState('');
  const [webhookSecret,      setWebhookSecret]      = useState('');
  const [webhookEvents,      setWebhookEvents]      = useState({
    'payout.success':     true,
    'payout.failed':      true,
    'payout.pending':     false,
    'collection.success': true,
    'usdt.deposit':       true,
  });
  const [webhookPayloadTab, setWebhookPayloadTab] = useState('payout');
  const [savingWebhook,     setSavingWebhook]     = useState(false);
  const [testingWebhook,    setTestingWebhook]    = useState(false);
  const [webhookLogs,       setWebhookLogs]       = useState([]);

  useEffect(() => { loadKeys(); loadWebhook(); }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api-keys/my-keys');
      setApiKey(res.data);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      else console.error(err);
    } finally { setLoading(false); }
  };

  const loadWebhook = async () => {
    try {
      const res  = await api.get('/v1/webhooks/config', { baseURL: 'https://kudibot.io/api' });
      const logs = await api.get('/v1/webhooks/logs',   { baseURL: 'https://kudibot.io/api' });
      if (res.data.configured) {
        setWebhookConfig(res.data.webhook);
        setWebhookUrl(res.data.webhook.url);
      }
      setWebhookLogs(logs.data.logs || []);
    } catch (err) { console.error(err); }
  };

  const saveWebhook = async () => {
    if (!webhookUrl) return toast.error('Enter webhook URL!');
    if (!webhookUrl.startsWith('https://')) return toast.error('URL must use HTTPS!');
    setSavingWebhook(true);
    try {
      const events = Object.entries(webhookEvents).filter(([,v]) => v).map(([k]) => k).join(',');
      const res = await api.post('/v1/webhooks/configure',
        { url: webhookUrl, secret: webhookSecret || undefined, events },
        { baseURL: 'https://kudibot.io/api' }
      );
      setWebhookConfig(res.data.webhook);
      if (res.data.webhook.secret) setWebhookSecret(res.data.webhook.secret);
      toast.success('Webhook configured!');
      loadWebhook();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed!');
    } finally { setSavingWebhook(false); }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await api.post('/v1/webhooks/test', {}, { baseURL: 'https://kudibot.io/api' });
      if (res.data.success) toast.success('Test webhook sent!');
      else toast.error('Test webhook failed!');
      loadWebhook();
    } catch { toast.error('Failed to send test webhook!'); }
    finally { setTestingWebhook(false); }
  };

  const generateKeys = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api-keys/generate');
      setNewKeys(res.data);
      setConfirmModal(false);
      loadKeys();
      toast.success('API keys generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed!');
    } finally { setGenerating(false); }
  };

  const revokeKey = async () => {
    try {
      await api.post('/api-keys/revoke');
      setApiKey(null); setNewKeys(null);
      toast.success('API key revoked!');
      loadKeys();
    } catch { toast.error('Failed to revoke key!'); }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  const TABS = [
    { id:'overview',  label:'Overview',  icon:<Globe size={14}/>    },
    { id:'endpoints', label:'Endpoints', icon:<List size={14}/>     },
    { id:'code',      label:'Code',      icon:<Terminal size={14}/> },
    { id:'webhook',   label:'Webhooks',  icon:<Bell size={14}/>     },
    { id:'keys',      label:'API Keys',  icon:<Key size={14}/>      },
  ];

  const Spinner = ({ color = '#000', size = 16 }) => (
    <div style={{ width: size, height: size, border: `2px solid ${color}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090B' }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${P}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
    </div>
  );

  if (accessDenied) return (
    <Layout>
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: `${P}12`, border: `2px solid ${P}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <Lock size={36} style={{ color: P2 }}/>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 12px' }}>API Access Restricted</h2>
          <p style={{ color: '#64748B', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Your account does not have API access enabled.<br/>Contact your admin to get access.
          </p>
          <div style={{ padding: '12px 16px', background: `${P}08`, border: `1px solid ${P}20`, borderRadius: 10, fontSize: 12, color: P2 }}>
            Contact admin to enable API access for your account.
          </div>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: '24px 24px 48px', maxWidth: 900, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg,${P},${P2})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Code size={20} style={{ color: '#000' }}/>
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: 0 }}>Developer API</h1>
              <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Integrate Kudibot payouts into your app</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
          {TABS.map(t => (
            <motion.button key={t.id} onClick={() => setActiveTab(t.id)} whileHover={{ scale: 1.02 }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
                background: activeTab === t.id ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.025)',
                border: activeTab === t.id ? 'none' : '1px solid rgba(255,255,255,0.06)',
                color: activeTab === t.id ? '#000' : '#94A3B8',
                fontSize: 12, fontWeight: 700,
              }}>
              {t.icon} {t.label}
            </motion.button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>
                <Globe size={16} style={{ color: P2 }}/> Base URL
              </h3>
              <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <code style={{ color: G, fontSize: 13 }}>https://kudibot.io/api/v1</code>
                <button onClick={() => copyText('https://kudibot.io/api/v1', 'URL')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                  <Copy size={14}/>
                </button>
              </div>
            </div>

            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>
                <Shield size={16} style={{ color: C }}/> Authentication
              </h3>
              <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>All requests require two headers:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { header: 'X-Api-Key',    desc: 'Your public API key' },
                  { header: 'X-Secret-Key', desc: 'Your secret key'     },
                ].map((h, i) => (
                  <div key={i} style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <code style={{ color: G, fontSize: 12, fontWeight: 700 }}>{h.header}</code>
                    <span style={{ color: '#475569', fontSize: 12 }}>{h.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }} className="dev-grid">
              {[
                { icon: <Globe size={18}/>,    title: 'Live Rates',     desc: 'Bybit P2P rates',         color: G         },
                { icon: <Zap size={18}/>,      title: 'Fast Payouts',   desc: 'Bank & mobile money',     color: C         },
                { icon: <Shield size={18}/>,   title: 'Secure',         desc: 'HMAC-SHA256 auth',        color: P2        },
                { icon: <Bell size={18}/>,     title: 'Webhooks',       desc: 'Real-time notifications', color: '#a855f7' },
                { icon: <Globe size={18}/>,    title: '6 Currencies',   desc: 'NGN, GHS, KES & more',    color: R         },
                { icon: <Terminal size={18}/>, title: 'USDT Addresses', desc: 'Per-invoice deposits',    color: '#a855f7' },
              ].map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  style={{ ...CARD, padding: 16 }}>
                  <div style={{ color: f.color, marginBottom: 8 }}>{f.icon}</div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{f.title}</p>
                  <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>{f.desc}</p>
                </motion.div>
              ))}
            </div>

            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>
                <Globe size={16} style={{ color: '#a855f7' }}/> Supported Currencies & Methods
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <p style={{ fontSize: 11, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Currencies</p>
                  {[
                    { code: 'NGN', name: 'Nigerian Naira',     flag: '🇳🇬' },
                    { code: 'GHS', name: 'Ghanaian Cedi',      flag: '🇬🇭' },
                    { code: 'KES', name: 'Kenyan Shilling',    flag: '🇰🇪' },
                    { code: 'UGX', name: 'Ugandan Shilling',   flag: '🇺🇬' },
                    { code: 'ZAR', name: 'South African Rand', flag: '🇿🇦' },
                    { code: 'XOF', name: 'West African CFA',   flag: '🌍'  },
                  ].map((cur, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
                      <span>{cur.flag}</span>
                      <code style={{ color: G, fontWeight: 700 }}>{cur.code}</code>
                      <span style={{ color: '#64748B' }}>{cur.name}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p style={{ fontSize: 11, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Methods</p>
                  {[
                    { code: 'bank', name: 'Bank Transfer' },
                    { code: 'momo', name: 'Mobile Money'  },
                  ].map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
                      <CheckCircle size={12} style={{ color: G }}/>
                      <code style={{ color: C, fontWeight: 700 }}>{m.code}</code>
                      <span style={{ color: '#64748B' }}>{m.name}</span>
                    </div>
                  ))}
                  <p style={{ fontSize: 11, color: '#475569', margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: 1 }}>Webhook Events</p>
                  {[
                    { event: 'payout.success',     color: G         },
                    { event: 'payout.failed',      color: R         },
                    { event: 'payout.pending',     color: P2        },
                    { event: 'collection.success', color: C         },
                    { event: 'usdt.deposit',       color: '#a855f7' },
                  ].map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: e.color }}/>
                      <code style={{ color: e.color, fontWeight: 700, fontSize: 11 }}>{e.event}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Endpoints ── */}
        {activeTab === 'endpoints' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ ...CARD, padding: '12px 16px', border: `1px solid ${G}30` }}>
              <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: G, fontSize: 12, fontWeight: 700, margin: 0 }}>
                <BookOpen size={12}/> Full docs at{' '}
                <a href="https://kudibot.io/api/docs" target="_blank" rel="noreferrer"
                  style={{ color: P2, textDecoration: 'underline' }}>https://kudibot.io/api/docs</a>
              </p>
            </div>

            {[
              { group: 'Core',          color: C,         items: ENDPOINTS.slice(0, 4)   },
              { group: 'Banks',         color: '#a855f7', items: ENDPOINTS.slice(4, 6)   },
              { group: 'Payouts',       color: G,         items: ENDPOINTS.slice(6, 8)   },
              { group: 'Collection',    color: C,         items: ENDPOINTS.slice(8, 9)   },
              { group: 'History',       color: P2,        items: ENDPOINTS.slice(9, 10)  },
              { group: 'USDT Addresses',color: '#a855f7', items: ENDPOINTS.slice(10, 13) },
              { group: 'Webhooks',      color: R,         items: ENDPOINTS.slice(13, 18) },
            ].map((g, gi) => (
              <div key={gi}>
                <p style={{ fontSize: 11, fontWeight: 700, color: g.color, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 8px' }}>{g.group}</p>
                {g.items.map((e, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    style={{ ...CARD, padding: 12, display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6, flexShrink: 0,
                      background: e.method === 'GET' ? `${C}18` : `${G}18`,
                      color: e.method === 'GET' ? C : G,
                    }}>{e.method}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <code style={{ color: '#fff', fontSize: 12, fontWeight: 700, wordBreak: 'break-all' }}>{e.path}</code>
                      <p style={{ color: '#64748B', fontSize: 12, margin: '2px 0 0' }}>{e.desc}</p>
                    </div>
                    <button onClick={() => copyText(`https://kudibot.io${e.path}`, 'URL')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', flexShrink: 0 }}>
                      <Copy size={12}/>
                    </button>
                  </motion.div>
                ))}
              </div>
            ))}

            {/* POST /withdraw body */}
            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>
                <Terminal size={14} style={{ color: G }}/> POST /withdraw — Request Body
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { field: 'amount_fiat',    type: 'number', req: true,  desc: 'Amount in local currency'            },
                  { field: 'currency',       type: 'string', req: true,  desc: 'NGN, GHS, KES, UGX, ZAR, XOF'       },
                  { field: 'method',         type: 'string', req: true,  desc: 'bank or momo'                        },
                  { field: 'account_number', type: 'string', req: true,  desc: 'Bank account or phone number'        },
                  { field: 'account_name',   type: 'string', req: true,  desc: 'Account holder name'                 },
                  { field: 'bank_code',      type: 'string', req: true,  desc: 'Bank/MoMo code from /banks endpoint' },
                  { field: 'bank_name',      type: 'string', req: false, desc: 'Bank name (optional)'                },
                  { field: 'phone_number',   type: 'string', req: false, desc: 'Phone number (momo)'                 },
                  { field: 'reference',      type: 'string', req: false, desc: 'Your unique reference ID'            },
                  { field: 'narration',      type: 'string', req: false, desc: 'Payment description'                 },
                ].map((f, i) => (
                  <div key={i} style={{ ...INNER, padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <code style={{ color: G, fontSize: 11, fontWeight: 700, width: 130, flexShrink: 0 }}>{f.field}</code>
                    <code style={{ color: C, fontSize: 11, width: 52, flexShrink: 0 }}>{f.type}</code>
                    <span style={{ fontSize: 11, width: 56, flexShrink: 0, color: f.req ? R : '#475569' }}>{f.req ? 'required' : 'optional'}</span>
                    <span style={{ fontSize: 11, color: '#64748B' }}>{f.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* POST /usdt/address body */}
            <div style={{ ...CARD, padding: 20, border: '1px solid rgba(168,85,247,0.2)' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
                <Terminal size={14} style={{ color: '#a855f7' }}/> POST /usdt/address — Request Body
              </h3>
              <p style={{ fontSize: 12, color: '#64748B', marginBottom: 12, lineHeight: 1.6 }}>
                Generates a unique TRC20 address. Same <code style={{ color: '#a855f7' }}>reference</code> always returns the same address. Fires <code style={{ color: '#a855f7' }}>usdt.deposit</code> webhook when payment arrives.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { field: 'reference', type: 'string', req: true,  desc: 'Your unique ID (invoice ID, order ID, customer ID)' },
                  { field: 'label',     type: 'string', req: false, desc: 'Human-readable label for your records'              },
                ].map((f, i) => (
                  <div key={i} style={{ ...INNER, padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <code style={{ color: G, fontSize: 11, fontWeight: 700, width: 130, flexShrink: 0 }}>{f.field}</code>
                    <code style={{ color: C, fontSize: 11, width: 52, flexShrink: 0 }}>{f.type}</code>
                    <span style={{ fontSize: 11, width: 56, flexShrink: 0, color: f.req ? R : '#475569' }}>{f.req ? 'required' : 'optional'}</span>
                    <span style={{ fontSize: 11, color: '#64748B' }}>{f.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* POST /collect body */}
            <div style={{ ...CARD, padding: 20, border: `1px solid ${C}20` }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
                <Terminal size={14} style={{ color: C }}/> POST /collect — Request Body
              </h3>
              <p style={{ fontSize: 12, color: '#64748B', marginBottom: 12 }}>
                Sends a payment prompt. Fires <code style={{ color: C }}>collection.success</code> webhook when approved.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { field: 'amount_ghs',    type: 'number', req: true,  desc: 'Amount to collect in GHS'              },
                  { field: 'phone',         type: 'string', req: true,  desc: 'Customer phone number'                  },
                  { field: 'subscriber_no', type: 'string', req: true,  desc: 'Subscriber number (usually same as phone)' },
                  { field: 'network',       type: 'string', req: true,  desc: 'MTN | VODAFONE | AIRTELTIGO'            },
                  { field: 'customer_name', type: 'string', req: true,  desc: 'Customer name on MoMo account'          },
                  { field: 'email',         type: 'string', req: false, desc: 'Customer email (optional)'              },
                  { field: 'reference',     type: 'string', req: false, desc: 'Your unique reference ID'               },
                  { field: 'description',   type: 'string', req: false, desc: 'Payment description'                    },
                ].map((f, i) => (
                  <div key={i} style={{ ...INNER, padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <code style={{ color: G, fontSize: 11, fontWeight: 700, width: 130, flexShrink: 0 }}>{f.field}</code>
                    <code style={{ color: C, fontSize: 11, width: 52, flexShrink: 0 }}>{f.type}</code>
                    <span style={{ fontSize: 11, width: 56, flexShrink: 0, color: f.req ? R : '#475569' }}>{f.req ? 'required' : 'optional'}</span>
                    <span style={{ fontSize: 11, color: '#64748B' }}>{f.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Code ── */}
        {activeTab === 'code' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.keys(CODE_EXAMPLES).map(k => (
                <motion.button key={k} onClick={() => setActiveCode(k)} whileHover={{ scale: 1.02 }}
                  style={{
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    background: activeCode === k ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.025)',
                    border: activeCode === k ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    color: activeCode === k ? '#000' : '#94A3B8',
                  }}>
                  {k === 'momo'        ? 'Mobile Money' :
                   k === 'collect'     ? 'GHS Collection' :
                   k === 'verify'      ? 'Verify Signature' :
                   k === 'banks'       ? 'Get Banks' :
                   k === 'usdt_address'? 'USDT Address' :
                   k.charAt(0).toUpperCase() + k.slice(1)}
                </motion.button>
              ))}
            </div>

            <div style={{ ...CARD, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Terminal size={14} style={{ color: G }}/>
                  <span style={{ color: '#64748B', fontSize: 12, fontWeight: 700 }}>
                    {activeCode === 'verify' ? 'Python Example' : 'cURL Example'}
                  </span>
                </div>
                <button onClick={() => copyText(CODE_EXAMPLES[activeCode], 'Code')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 11 }}>
                  {copied === 'Code' ? <CheckCircle size={12} style={{ color: G }}/> : <Copy size={12}/>}
                  {copied === 'Code' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre style={{ padding: 16, fontSize: 11, color: '#CBD5E1', overflowX: 'auto', lineHeight: 1.7, margin: 0 }}>
                <code>{CODE_EXAMPLES[activeCode]}</code>
              </pre>
            </div>
          </motion.div>
        )}

        {/* ── Webhooks ── */}
        {activeTab === 'webhook' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>
                <Bell size={16} style={{ color: '#a855f7' }}/> How Webhooks Work
              </h3>
              <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
                Kudibot sends a POST request to your URL when a payout status changes. Each request is signed with HMAC-SHA256 for security.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { event: 'payout.success',     desc: 'Payout completed successfully',            color: G         },
                  { event: 'payout.failed',      desc: 'Payout failed or rejected',                color: R         },
                  { event: 'payout.pending',     desc: 'Payout is queued/pending',                 color: P2        },
                  { event: 'collection.success', desc: 'GHS MoMo payment approved by customer',    color: C         },
                  { event: 'usdt.deposit',       desc: 'USDT received at a generated sub-address', color: '#a855f7' },
                ].map((e, i) => (
                  <div key={i} style={{ ...INNER, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: e.color, flexShrink: 0 }}/>
                    <code style={{ fontSize: 11, fontWeight: 700, color: e.color }}>{e.event}</code>
                    <span style={{ fontSize: 12, color: '#64748B' }}>{e.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payload examples */}
            <div style={{ ...CARD, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { id: 'payout',     label: 'Payout',       color: G         },
                    { id: 'collection', label: 'Collection',   color: C         },
                    { id: 'usdt',       label: 'USDT Deposit', color: '#a855f7' },
                  ].map(t => (
                    <button key={t.id} onClick={() => setWebhookPayloadTab(t.id)}
                      style={{
                        padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, border: 'none',
                        background: webhookPayloadTab === t.id ? t.color : 'transparent',
                        color: webhookPayloadTab === t.id ? '#000' : '#64748B',
                      }}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => copyText(
                    webhookPayloadTab === 'payout'     ? WEBHOOK_PAYLOAD_PAYOUT :
                    webhookPayloadTab === 'collection' ? WEBHOOK_PAYLOAD_COLLECTION :
                    WEBHOOK_PAYLOAD_USDT, 'Payload'
                  )}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 11 }}>
                  <Copy size={12}/> Copy
                </button>
              </div>
              <pre style={{ padding: 16, fontSize: 11, color: '#CBD5E1', overflowX: 'auto', lineHeight: 1.7, margin: 0 }}>
                <code>
                  {webhookPayloadTab === 'payout'     ? WEBHOOK_PAYLOAD_PAYOUT :
                   webhookPayloadTab === 'collection' ? WEBHOOK_PAYLOAD_COLLECTION :
                   WEBHOOK_PAYLOAD_USDT}
                </code>
              </pre>
            </div>

            {/* Signature verification */}
            <div style={{ ...CARD, padding: 20, border: `1px solid ${P}20` }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>
                <Shield size={16} style={{ color: P2 }}/> Signature Verification
              </h3>
              <p style={{ color: '#64748B', fontSize: 13, marginBottom: 12 }}>
                Every webhook includes <code style={{ color: G }}>X-Kudibot-Signature</code> header. Verify it to ensure the request is from Kudibot.
              </p>
              <pre style={{ ...INNER, padding: 16, fontSize: 11, color: '#CBD5E1', overflowX: 'auto', lineHeight: 1.7, margin: 0 }}>
                <code>{`import hmac, hashlib

def verify(payload, signature, secret):
    expected = hmac.new(
        secret.encode(), payload.encode(), hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)`}
                </code>
              </pre>
            </div>

            {/* Configure */}
            <div style={{ ...CARD, padding: 20 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>
                <Bell size={16} style={{ color: G }}/> {webhookConfig ? 'Update Webhook' : 'Configure Webhook'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748B', display: 'block', marginBottom: 6 }}>Webhook URL (HTTPS required)</label>
                  <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                    placeholder="https://yourapp.com/webhooks/kudibot" style={inputStyle}/>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748B', display: 'block', marginBottom: 6 }}>Secret Key (optional — auto-generated if empty)</label>
                  <input value={webhookSecret} onChange={e => setWebhookSecret(e.target.value)}
                    placeholder="your_webhook_secret" style={inputStyle}/>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748B', display: 'block', marginBottom: 8 }}>Events to Subscribe</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Object.entries(webhookEvents).map(([event, checked]) => (
                      <label key={event} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked}
                          onChange={e => setWebhookEvents(prev => ({ ...prev, [event]: e.target.checked }))}
                          style={{ width: 14, height: 14, accentColor: P }}/>
                        <code style={{
                          fontSize: 11, fontWeight: 700,
                          color: event === 'payout.success'     ? G :
                                 event === 'payout.failed'      ? R :
                                 event === 'collection.success' ? C :
                                 event === 'usdt.deposit'       ? '#a855f7' : P2,
                        }}>{event}</code>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={saveWebhook} disabled={savingWebhook}
                    style={{
                      flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg,${P},${P2})`,
                      color: '#000', fontSize: 13, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}>
                    {savingWebhook ? <Spinner/> : <><Bell size={14}/> {webhookConfig ? 'Update' : 'Configure'}</>}
                  </motion.button>
                  {webhookConfig && (
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={testWebhook} disabled={testingWebhook}
                      style={{
                        padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                        background: `${G}12`, border: `1px solid ${G}30`,
                        color: G, fontSize: 13, fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                      {testingWebhook ? <Spinner color={G}/> : <><Zap size={14}/> Test</>}
                    </motion.button>
                  )}
                </div>
              </div>
            </div>

            {/* Logs */}
            {webhookLogs.length > 0 && (
              <div style={{ ...CARD, padding: 20 }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>
                  <Activity size={16} style={{ color: C }}/> Delivery Logs
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {webhookLogs.slice(0, 10).map((log, i) => (
                    <div key={i} style={{ ...INNER, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: log.status === 'delivered' ? G : R, flexShrink: 0 }}/>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{log.event}</code>
                          <span style={{
                            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                            background: log.status === 'delivered' ? `${G}14` : `${R}14`,
                            color: log.status === 'delivered' ? G : R,
                          }}>{log.status}</span>
                          {log.response_code && <span style={{ color: '#475569', fontSize: 11 }}>{log.response_code}</span>}
                        </div>
                        <p style={{ color: '#475569', fontSize: 11, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.reference}</p>
                      </div>
                      <span style={{ color: '#475569', fontSize: 11, flexShrink: 0 }}>
                        {new Date(log.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ── API Keys ── */}
        {activeTab === 'keys' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <AnimatePresence>
              {newKeys && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  style={{ ...CARD, padding: 20, border: `1px solid ${P}30`, background: `${P}06` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertTriangle size={16} style={{ color: P2 }}/>
                      <span style={{ color: P2, fontWeight: 700, fontSize: 13 }}>Save Your Secret Key!</span>
                    </div>
                    <button onClick={() => setNewKeys(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                      <X size={16}/>
                    </button>
                  </div>
                  <p style={{ color: '#64748B', fontSize: 12, marginBottom: 16 }}>Your secret key will NOT be shown again!</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Live Public Key', value: newKeys.public_key, id: 'pub' },
                      { label: 'Live Secret Key', value: newKeys.secret_key, id: 'sec' },
                      ...(newKeys.test_public_key ? [
                        { label: 'Test Public Key (sandbox)', value: newKeys.test_public_key, id: 'tpub' },
                        { label: 'Test Secret Key (sandbox)', value: newKeys.test_secret_key, id: 'tsec' },
                      ] : []),
                    ].map((k, i) => (
                      <div key={i}>
                        <p style={{ color: '#64748B', fontSize: 12, marginBottom: 6 }}>{k.label}</p>
                        <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ color: G, fontSize: 11, flex: 1, wordBreak: 'break-all' }}>
                            {k.id.endsWith('sec') && !showSecret ? '•'.repeat(32) : k.value}
                          </code>
                          {k.id.endsWith('sec') && (
                            <button onClick={() => setShowSecret(!showSecret)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                              {showSecret ? <EyeOff size={14}/> : <Eye size={14}/>}
                            </button>
                          )}
                          <button onClick={() => copyText(k.value, k.label)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                            {copied === k.label ? <CheckCircle size={14} style={{ color: G }}/> : <Copy size={14}/>}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {apiKey?.has_api_key ? (
              <div style={{ ...CARD, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>
                    <Key size={16} style={{ color: G }}/> Your API Key
                  </h3>
                  <span style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 6, fontWeight: 700,
                    background: apiKey.is_active ? `${G}14` : `${R}14`,
                    color: apiKey.is_active ? G : R,
                  }}>
                    {apiKey.is_active ? '● Active' : '● Inactive'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                  <div>
                    <p style={{ color: '#64748B', fontSize: 12, marginBottom: 6 }}>Public Key</p>
                    <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ color: G, fontSize: 11, flex: 1, wordBreak: 'break-all' }}>{apiKey.public_key}</code>
                      <button onClick={() => copyText(apiKey.public_key, 'Public Key')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                        {copied === 'Public Key' ? <CheckCircle size={14} style={{ color: G }}/> : <Copy size={14}/>}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p style={{ color: '#64748B', fontSize: 12, marginBottom: 6 }}>Secret Key</p>
                    <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ color: '#475569', fontSize: 11, flex: 1 }}>{'•'.repeat(32)} (hidden)</code>
                      <Lock size={14} style={{ color: '#475569' }}/>
                    </div>
                  </div>
                  {apiKey.test_public_key ? (
                    <div>
                      <p style={{ color: '#64748B', fontSize: 12, marginBottom: 6 }}>
                        Test Public Key{' '}
                        <span style={{ background: `${C}18`, color: C, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginLeft: 4 }}>SANDBOX</span>
                      </p>
                      <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ color: C, fontSize: 11, flex: 1, wordBreak: 'break-all' }}>{apiKey.test_public_key}</code>
                        <button onClick={() => copyText(apiKey.test_public_key, 'Test Public Key')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                          {copied === 'Test Public Key' ? <CheckCircle size={14} style={{ color: G }}/> : <Copy size={14}/>}
                        </button>
                      </div>
                      <p style={{ color: '#475569', fontSize: 11, marginTop: 6 }}>
                        Test keys simulate payouts, collections &amp; deposits — no real money moves. Docs: /docs.html#testmode
                      </p>
                    </div>
                  ) : (
                    <div style={{ ...INNER, padding: 12 }}>
                      <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>
                        No sandbox keys yet — click <b style={{ color: P2 }}>Regenerate</b> to also get test keys (kb_test_...) for integration testing.
                      </p>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Last Used', value: apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : 'Never' },
                      { label: 'Created',   value: apiKey.created_at   ? new Date(apiKey.created_at).toLocaleDateString()   : '—'   },
                    ].map((info, i) => (
                      <div key={i} style={{ ...INNER, padding: 12 }}>
                        <p style={{ color: '#475569', fontSize: 11, margin: '0 0 4px' }}>{info.label}</p>
                        <p style={{ color: '#fff', fontSize: 11, fontWeight: 700, margin: 0 }}>{info.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => setConfirmModal(true)} disabled={generating}
                    style={{
                      flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg,${P},${P2})`,
                      color: '#000', fontSize: 13, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}>
                    <RefreshCw size={14}/> Regenerate
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={revokeKey}
                    style={{
                      padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                      background: `${R}12`, border: `1px solid ${R}20`,
                      color: R, fontSize: 13, fontWeight: 700,
                    }}>
                    Revoke
                  </motion.button>
                </div>
              </div>
            ) : (
              <div style={{ ...CARD, padding: 40, textAlign: 'center' }}>
                <Key size={40} style={{ color: '#334155', margin: '0 auto 16px', display: 'block' }}/>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>No API Keys Yet</h3>
                <p style={{ color: '#64748B', fontSize: 13, marginBottom: 24 }}>
                  Generate your API keys to start integrating Kudibot payouts!
                </p>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={generateKeys} disabled={generating}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '12px 28px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg,${P},${P2})`,
                    color: '#000', fontSize: 13, fontWeight: 700,
                  }}>
                  {generating ? <Spinner/> : <><Key size={16}/> Generate API Keys</>}
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Confirm modal */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              style={{ ...CARD, padding: 24, border: `1px solid ${P}20`, maxWidth: 360, width: '100%', textAlign: 'center' }}>
              <AlertTriangle size={40} style={{ color: P2, margin: '0 auto 12px', display: 'block' }}/>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>Regenerate API Keys?</h3>
              <p style={{ color: '#64748B', fontSize: 13, marginBottom: 24 }}>Old keys will be invalidated immediately!</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setConfirmModal(false)}
                  style={{ flex: 1, padding: '12px 0', borderRadius: 10, cursor: 'pointer', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', color: '#94A3B8', fontSize: 13, fontWeight: 700 }}>
                  Cancel
                </button>
                <motion.button whileHover={{ scale: 1.02 }} onClick={generateKeys} disabled={generating}
                  style={{
                    flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg,${P},${P2})`,
                    color: '#000', fontSize: 13, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {generating ? <Spinner/> : 'Regenerate!'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 700px) { .dev-grid { grid-template-columns: 1fr 1fr !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
}
